/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { getOrientation } from 'get-orientation/browser';
import humanize from 'humanize-plus';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useEventCallback } from '../components/hooks/useEventCallback';
import { MAX_PHOTO_FILE_SIZE_IN_BYTES, ORIENTATION_TO_ANGLE } from '../constants/settings';
import { getRotatedImage, readFile } from './canvasUtils';

export const useFileInputChange = (cb: (data: string) => void) => {
  const { t } = useTranslation();

  const onFileInputChange = useEventCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      e.target.files = null;
      e.target.value = '';

      if (file.size > MAX_PHOTO_FILE_SIZE_IN_BYTES) {
        alert(
          t('warnings.exceeded_file_size_with_max', {
            maxSize: humanize.fileSize(MAX_PHOTO_FILE_SIZE_IN_BYTES),
          }),
        );
        return;
      }
      let imageDataUrl = await readFile(file);
      const orientation = await getOrientation(file);
      const rotation = ORIENTATION_TO_ANGLE[orientation as number] ?? ORIENTATION_TO_ANGLE.__fallback;

      if (rotation) {
        imageDataUrl = await (await getRotatedImage(imageDataUrl, rotation)).toBase64();
      }

      cb(imageDataUrl);
    }
  });

  return {
    onFileInputChange,
  };
};
